import type { User } from '@allemnii-pulse/shared';
import { useDocument, type LiveState } from './firestore.js';

export type UserProfile = Pick<User, 'displayName' | 'email' | 'photoURL' | 'status'>;

/** Live read of `users/{uid}` — readable to any signed-in user (firestore.rules), so this works
 * for rendering any actor/assignee/author name, not just the current user's own. */
export function useUserProfile(uid: string | null | undefined): LiveState<UserProfile> {
  return useDocument<UserProfile>(uid ? `users/${uid}` : null);
}

/** Audit-log entries can be written by the CLI as well as a human — see ActivityFeed. */
export function isSystemActor(uid: string): boolean {
  return uid.startsWith('cli:');
}

/**
 * "First name + family initial" per the product's display rule, e.g. "Ahmed A." Falls back to
 * the email's local part, then to a short id, so something readable always renders even before
 * the user's profile document has loaded (or for a uid with no profile at all).
 */
export function formatUserName(uid: string, profile?: UserProfile | null): string {
  if (isSystemActor(uid)) return uid.slice(4) || 'CLI';

  const displayName = profile?.displayName?.trim();
  if (displayName) {
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0]} ${parts[parts.length - 1]![0]!.toUpperCase()}.`;
    return parts[0]!;
  }

  const email = profile?.email?.trim();
  if (email) {
    const local = email.split('@')[0]!;
    return local.charAt(0).toUpperCase() + local.slice(1);
  }

  return `User ${uid.slice(0, 6)}`;
}

/** Two-letter avatar fallback for a formatted name, e.g. "Ahmed A." -> "AA". */
export function initialsFor(uid: string, profile?: UserProfile | null): string {
  if (isSystemActor(uid)) return 'CLI';
  const name = formatUserName(uid, profile);
  const parts = name.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]!.toUpperCase());
  return letters.join('') || '?';
}
